/**
 * Better Auth Client for Sanctuary Frontend
 *
 * Integrates with Clarity's Better Auth system for cross-subdomain authentication.
 *
 * IMPORTANT: Auth API calls use same-origin (empty baseURL) so Istio can proxy
 * them to clarity-web. This is required because:
 * - Session cookies have SameSite=Lax
 * - Cross-origin fetch requests (to app.claritycm.com) don't include cookies
 * - Same-origin requests to sanctuary.claritycm.com/api/auth/* are proxied via Istio
 */

import { createAuthClient } from "better-auth/react"
import { jwtClient } from "better-auth/client/plugins"

// Auth server URL for redirects (login page is on clarity-web)
const AUTH_REDIRECT_URL = import.meta.env.VITE_AUTH_URL || "https://app.claritycm.com"

// Create Better Auth client with JWT plugin for API token retrieval
// IMPORTANT: Use empty baseURL for same-origin requests, proxied to clarity-web via Istio
export const authClient = createAuthClient({
  baseURL: "", // Same-origin - Istio routes /api/auth/* to clarity-web
  plugins: [
    jwtClient(),
  ],
})

/**
 * Session hook from Better Auth.
 */
export const useSession = authClient.useSession

/**
 * Get JWT token for API calls.
 */
export async function getApiToken(): Promise<string | null> {
  try {
    const result = await authClient.token()

    if (result.error) {
      console.error("Failed to get JWT token:", result.error)
      return null
    }

    return result.data?.token || null
  } catch (error) {
    console.error('Failed to get API token:', error)
    return null
  }
}

/**
 * Sign out the current user.
 */
export async function signOut(options?: { redirectTo?: string }): Promise<void> {
  try {
    await authClient.signOut()
    const redirectTo = options?.redirectTo || `${AUTH_REDIRECT_URL}/login`
    window.location.href = redirectTo
  } catch (error) {
    console.error('Sign out failed:', error)
    window.location.href = `${AUTH_REDIRECT_URL}/login`
  }
}

/**
 * Redirect to login page with callback URL.
 */
export function redirectToLogin(callbackUrl?: string): void {
  const callback = callbackUrl || window.location.href
  const loginUrl = `${AUTH_REDIRECT_URL}/login?callbackUrl=${encodeURIComponent(callback)}`
  window.location.href = loginUrl
}

/**
 * Parse roles from session user.
 */
function parseRoles(user: { roles?: string | string[] } | null | undefined): string[] {
  if (!user?.roles) {
    return []
  }
  if (Array.isArray(user.roles)) {
    return user.roles
  }
  if (typeof user.roles === 'string') {
    try {
      const parsed = JSON.parse(user.roles)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

/**
 * Check if user has a specific role.
 */
export function hasRole(
  session: unknown,
  role: string
): boolean {
  const user = (session as { user?: { roles?: string | string[] } } | null)?.user
  const roles = parseRoles(user)
  return roles.includes(role)
}

/**
 * Check if user can access Sanctuary.
 *
 * Allowed roles: admin, soft_room_responder, field_reporter
 */
export function canAccessSanctuary(
  session: unknown
): boolean {
  const user = (session as { user?: { roles?: string | string[] } } | null)?.user
  const roles = parseRoles(user)
  if (roles.length === 0) {
    // If no roles, still allow access (default behavior)
    return true
  }
  const allowedRoles = ['admin', 'soft_room_responder', 'field_reporter', 'case_manager']
  return roles.some(role => allowedRoles.includes(role))
}

/**
 * Type definitions
 */
export interface SessionUser {
  id: string
  email: string
  name?: string
  image?: string
  roles?: string[]
  organizationId?: string
}

export interface Session {
  user: SessionUser
  expires: string
}

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated'

/**
 * Get parsed roles from a session object.
 */
export function getUserRoles(session: unknown): string[] {
  const user = (session as { user?: { roles?: string | string[] } } | null)?.user
  return parseRoles(user)
}
